import { updateOrder, buildSavePayload } from "@/services/ordersService";
import { USER_EDIT_BUS_EVENT } from "@/util/autosaveEvents";
import { Message } from "element-ui";

/**
 * Intervallo minimo accettato, in secondi: sotto questa soglia si tratta di una
 * configurazione errata e si userebbe l'API come un ping.
 */
const MIN_AUTOSAVE_INTERVAL_SECONDS = 5;

/**
 * Tempo massimo di attesa del salvataggio finale quando si lascia la pagina.
 * Serve perché axios è configurato senza timeout: senza questo limite una request
 * appesa bloccherebbe la navigazione. La request prosegue comunque in background.
 */
const FLUSH_TIMEOUT_MS = 5000;

/** Eventi che testimoniano una modifica reale dell'utente (non una normalizzazione dell'editor). */
const USER_EDIT_EVENTS = ["keydown", "paste", "cut", "drop"];

/** Stati in cui il contenuto è in lavorazione e non va toccato. */
const BLOCKING_STATUSES = [
  "waiting_queue",
  "waiting_generation",
  "processing",
  "failed",
];

/**
 * Salvataggio automatico del contenuto di un order detail.
 *
 * Comportamento:
 * - il timer parte alla prima modifica e salva a ogni intervallo finché ci sono modifiche
 *   non salvate; si ferma dopo un salvataggio riuscito e riparte alla modifica successiva.
 *   L'intervallo è `VUE_APP_AUTOSAVE_INTERVAL` in secondi; se la chiave manca o vale 0
 *   il salvataggio automatico è disattivato e resta solo quello manuale;
 * - "modificato" significa diverso dall'ultimo stato salvato (baseline), non
 *   "l'editor ha emesso un evento": i componenti in sola lettura non generano mai
 *   un salvataggio, e la normalizzazione del contenuto dopo il fetch non produce falsi positivi;
 * - finché l'utente non ha fatto una modifica reale l'autosave è disarmato e la baseline
 *   segue il contenuto: serve perché Quill riscrive l'HTML appena montato (normalizzazione)
 *   e senza questo si otterrebbe un salvataggio "fantasma" su un documento solo aperto;
 * - il payload è costruito da `buildSavePayload`, lo stesso usato dal salvataggio manuale;
 * - l'autosave non ricarica l'order nello store (`refetch: false`), altrimenti l'editor
 *   verrebbe rimontato e l'utente perderebbe cursore e selezione;
 * - lato backend l'autosave non crea versioni in history (solo il salvataggio manuale lo fa).
 *
 * Il componente che lo usa deve:
 * - esporre `order` (e, se presente, `task`);
 * - chiamare `resetAutosaveBaseline()` dopo ogni ricarica dell'order (fetch, rigenerazione);
 * - agganciare `onOrderSavedManually()` all'evento `saved` di ContentToolbar;
 * - chiamare `flushAutosave()` prima di lasciare la pagina.
 */
export default {
  data() {
    return {
      unsavedContent: false,
      autosaveArmed: false,
      autosaveBaseline: null,
      autosaveTimer: null,
      autosaveSaving: false,
      autosaveInFlight: null,
      autosaveSkipTicks: 0,
      autosaveToast: null,
    };
  },

  computed: {
    /**
     * Intervallo del salvataggio automatico, in secondi, da `VUE_APP_AUTOSAVE_INTERVAL`.
     * Chiave assente, vuota o non valida => 0, cioè autosave disattivato:
     * va abilitato esplicitamente indicando l'intervallo.
     */
    autosaveIntervalSeconds() {
      const configured = process.env.VUE_APP_AUTOSAVE_INTERVAL;

      if (configured === undefined || configured === "") {
        return 0;
      }

      const seconds = Number(configured);

      if (!Number.isFinite(seconds) || seconds < 0) {
        console.warn(
          `VUE_APP_AUTOSAVE_INTERVAL non valido ("${configured}"): autosave disattivato`,
        );
        return 0;
      }

      if (seconds === 0) {
        return 0;
      }

      return Math.max(seconds, MIN_AUTOSAVE_INTERVAL_SECONDS);
    },

    autosaveEnabled() {
      return this.autosaveIntervalSeconds > 0;
    },

    autosaveUser() {
      return this.$store.getters.getUser;
    },

    /** Contenuto gestito dal componente padre, per i tool che non usano `order.content`. */
    autosaveParentContent() {
      return "";
    },

    autosaveAllowed() {
      const order = this.order;

      if (!this.autosaveEnabled || !order) {
        return false;
      }

      // I tool media non hanno contenuto editabile (e nemmeno il salvataggio manuale)
      if (order.category !== "text") {
        return false;
      }

      if (order.trashed_at || order.deleted_at) {
        return false;
      }

      if (BLOCKING_STATUSES.includes(order.status)) {
        return false;
      }

      // Fuori dal flusso task il contenuto è editabile solo quando è accettato
      if (order.status !== "accepted" && !this.task) {
        return false;
      }

      // Task assegnato a un altro utente: editor in sola lettura (vedi TextContent.vue)
      if (
        this.task &&
        this.task.assignee_id != this.autosaveUser?.id &&
        this.autosaveUser?.role === "user"
      ) {
        return false;
      }

      return true;
    },
  },

  watch: {
    "order.content"() {
      this.autosaveCheckDirty();
    },
    "order.title"() {
      this.autosaveCheckDirty();
    },
  },

  methods: {
    /** Stato salvabile corrente, o null se non c'è nulla da salvare. */
    autosaveCurrentState() {
      return buildSavePayload(this.order, this.autosaveParentContent);
    },

    /**
     * Confronta lo stato corrente con l'ultimo salvato e aggiorna `unsavedContent`,
     * avviando o fermando il timer di conseguenza.
     */
    autosaveCheckDirty() {
      const current = this.autosaveCurrentState();

      if (!current) {
        return;
      }

      // Primo passaggio (o order appena caricato): lo stato corrente diventa il riferimento
      if (!this.autosaveBaseline) {
        this.autosaveBaseline = current;
        this.unsavedContent = false;
        return;
      }

      // Nessuna modifica dell'utente finora: quello che cambia è l'editor che si inizializza
      if (!this.autosaveArmed) {
        this.autosaveBaseline = current;
        this.unsavedContent = false;
        return;
      }

      this.unsavedContent =
        current.content !== this.autosaveBaseline.content ||
        current.title !== this.autosaveBaseline.title;

      if (this.unsavedContent) {
        if (this.autosaveAllowed) {
          this.startAutosaveTimer();
        }
      } else {
        this.stopAutosaveTimer();
      }
    },

    /** Allinea il riferimento allo stato attuale dell'order (dopo fetch o salvataggio). */
    resetAutosaveBaseline() {
      this.autosaveBaseline = this.autosaveCurrentState();
      this.unsavedContent = false;
    },

    /**
     * Registra che l'utente ha fatto una modifica reale: da qui in poi le variazioni
     * di contenuto e titolo valgono come "da salvare".
     */
    autosaveArm() {
      if (this.autosaveArmed) {
        return;
      }

      this.autosaveArmed = true;
      this.autosaveRemoveArmListeners();
    },

    autosaveAddArmListeners() {
      USER_EDIT_EVENTS.forEach((event) => {
        document.addEventListener(event, this.autosaveArm, true);
      });
    },

    autosaveRemoveArmListeners() {
      USER_EDIT_EVENTS.forEach((event) => {
        document.removeEventListener(event, this.autosaveArm, true);
      });
    },

    startAutosaveTimer() {
      if (this.autosaveTimer) {
        return;
      }

      this.autosaveTimer = setInterval(
        this.autosaveTick,
        this.autosaveIntervalSeconds * 1000,
      );
    },

    stopAutosaveTimer() {
      if (this.autosaveTimer) {
        clearInterval(this.autosaveTimer);
        this.autosaveTimer = null;
      }

      this.autosaveSkipTicks = 0;
    },

    async autosaveTick() {
      // Dopo un errore si salta un giro invece di ritentare a raffica
      if (this.autosaveSkipTicks > 0) {
        this.autosaveSkipTicks--;
        return;
      }

      if (this.autosaveSaving || !this.unsavedContent || !this.autosaveAllowed) {
        return;
      }

      await this.autosaveNow();
    },

    async autosaveNow() {
      const payload = this.autosaveCurrentState();

      if (!payload) {
        return false;
      }

      this.autosaveSaving = true;
      let resolveInFlight;
      this.autosaveInFlight = new Promise((resolve) => {
        resolveInFlight = resolve;
      });

      try {
        const response = await updateOrder(
          payload.orderDetailId,
          payload.content,
          payload.title,
          { refetch: false, autosave: true },
        );

        if (response?.status !== "success") {
          throw new Error(response?.message ?? "Autosave failed");
        }

        // Riferimento = quello che è stato effettivamente inviato: se l'utente ha
        // scritto durante la request, il contenuto resta "da salvare" per il tick dopo
        this.autosaveBaseline = {
          content: payload.content,
          title: payload.title,
        };

        this.autosaveNotifySaved();
        this.autosaveCheckDirty();

        return true;
      } catch (error) {
        console.error("Autosave failed:", error);
        this.autosaveSkipTicks = 1;
        Message({
          showClose: true,
          message: "Autosave failed, your latest changes are not saved yet",
          type: "error",
        });

        return false;
      } finally {
        this.autosaveSaving = false;
        this.autosaveInFlight = null;
        resolveInFlight();
      }
    },

    autosaveNotifySaved() {
      if (this.autosaveToast?.close) {
        this.autosaveToast.close();
      }

      this.autosaveToast = Message({
        message: "Saved",
        type: "success",
        duration: 2000,
        showClose: false,
      });
    },

    /** Salvataggio manuale andato a buon fine: l'order è già stato ricaricato dallo store. */
    onOrderSavedManually() {
      this.stopAutosaveTimer();
      this.resetAutosaveBaseline();
    },

    /** Salva subito le modifiche pendenti (uscita dalla pagina, tab nascosta). */
    async flushAutosave() {
      if (!this.autosaveAllowed) {
        return;
      }

      const flush = async () => {
        // Un salvataggio già in corso non contiene ciò che l'utente ha scritto
        // nel frattempo: si attende e si ricontrolla
        if (this.autosaveInFlight) {
          await this.autosaveInFlight;
        }

        if (!this.unsavedContent) {
          return;
        }

        await this.autosaveNow();
      };

      await Promise.race([
        flush(),
        new Promise((resolve) => setTimeout(resolve, FLUSH_TIMEOUT_MS)),
      ]);
    },

    autosaveOnVisibilityChange() {
      if (document.visibilityState === "hidden") {
        this.flushAutosave();
      }
    },
  },

  mounted() {
    this.autosaveAddArmListeners();
    this.$root.$on(USER_EDIT_BUS_EVENT, this.autosaveArm);
    document.addEventListener(
      "visibilitychange",
      this.autosaveOnVisibilityChange,
    );
  },

  beforeDestroy() {
    this.autosaveRemoveArmListeners();
    this.$root.$off(USER_EDIT_BUS_EVENT, this.autosaveArm);
    document.removeEventListener(
      "visibilitychange",
      this.autosaveOnVisibilityChange,
    );
    this.stopAutosaveTimer();
  },
};
